import React, { useContext, useState, useEffect, useMemo } from 'react';
import { DatabaseContext } from '../../redux/DatabaseStore';
import { postRequest } from '../../api/ApiController';
import AuthContext from '../../components/AuthContext';
import UtilContext from '../../components/UtilContext';
import { LocaleContext } from '../../components/LocaleContext';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';

/** Hodnotu vždy vráť ako string (konzistentné s load_module_settings a Dropdown optionValue). */
function getGlobalSetting(module_settings, key) {
  const row = (module_settings || []).find(s => s.module === 'global' && s.key === key);
  const v = row ? row.value : null;
  if (v == null) return null;
  return typeof v === 'string' ? v : String(v);
}

function getGlobalSettingRow(module_settings, key) {
  return (module_settings || []).find(s => s.module === 'global' && s.key === key);
}

function parseOptions(options) {
  if (Array.isArray(options)) return options;
  if (typeof options === 'string') {
    try {
      const parsed = JSON.parse(options);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
}

const PAGE_SIZE_OPTIONS = [
  { label: '10', value: 10 },
  { label: '25', value: 25 },
  { label: '50', value: 50 },
  { label: '100', value: 100 },
  { label: '200', value: 200 },
];

/** Fallback meny / časové pásma (label cez `t(labelKey)`). Z API zostávajú labely z DB. */
const CURRENCY_FALLBACK = [
  { value: '€', labelKey: 'UnitsSettings.Currency.EUR' },
  { value: 'CZK', labelKey: 'UnitsSettings.Currency.CZK' },
  { value: '$', labelKey: 'UnitsSettings.Currency.USD' },
  { value: '£', labelKey: 'UnitsSettings.Currency.GBP' },
  { value: 'CHF', labelKey: 'UnitsSettings.Currency.CHF' },
  { value: 'zł', labelKey: 'UnitsSettings.Currency.PLN' },
  { value: 'Ft', labelKey: 'UnitsSettings.Currency.HUF' },
];

const TIMEZONE_FALLBACK = [
  { value: 'Europe/Bratislava', labelKey: 'UnitsSettings.Timezone.EuropeBratislava' },
  { value: 'Europe/Prague', labelKey: 'UnitsSettings.Timezone.EuropePrague' },
  { value: 'Europe/Budapest', labelKey: 'UnitsSettings.Timezone.EuropeBudapest' },
  { value: 'Europe/Warsaw', labelKey: 'UnitsSettings.Timezone.EuropeWarsaw' },
  { value: 'Europe/Vienna', labelKey: 'UnitsSettings.Timezone.EuropeVienna' },
  { value: 'Europe/Berlin', labelKey: 'UnitsSettings.Timezone.EuropeBerlin' },
  { value: 'UTC', labelKey: 'UnitsSettings.Timezone.UTC' },
];

const DATE_FORMAT_I18N_KEYS = [
  { value: 'dd.MM.yyyy', key: 'UnitsSettings.DateFormat.ddMMYYYY' },
  { value: 'dd/MM/yyyy', key: 'UnitsSettings.DateFormat.ddMMyyyySlash' },
  { value: 'yyyy-MM-dd', key: 'UnitsSettings.DateFormat.yyyyMMdd' },
  { value: 'MM/dd/yyyy', key: 'UnitsSettings.DateFormat.MMddyyyy' },
  { value: 'd. M. yyyy', key: 'UnitsSettings.DateFormat.dMyyyy' },
];

const OPENWEATHER_API_KEY = '24110dc31c01b76eeef9d9264d0483e8';

export default function UnitsSettings() {
  const [state, dispatch] = useContext(DatabaseContext);
  const { user } = useContext(AuthContext);
  const { toast, openChosenNotificationPreferences } = useContext(UtilContext);
  const { t } = useContext(LocaleContext);
  const module_settings = state.module_settings || [];

  const defaultDateFormatOptions = useMemo(
    () => DATE_FORMAT_I18N_KEYS.map((row) => ({ value: row.value, label: t(row.key) })),
    [t]
  );

  const defaultTemperatureOptions = useMemo(
    () => [
      { value: '°C', label: t('UnitsSettings.Temp.Celsius') },
      { value: 'K', label: t('UnitsSettings.Temp.Kelvin') },
      { value: '°F', label: t('UnitsSettings.Temp.Fahrenheit') },
    ],
    [t]
  );

  const defaultCurrencyOptions = useMemo(
    () => CURRENCY_FALLBACK.map((o) => ({ value: o.value, label: t(o.labelKey) })),
    [t]
  );
  const defaultTimezoneOptions = useMemo(
    () => TIMEZONE_FALLBACK.map((o) => ({ value: o.value, label: t(o.labelKey) })),
    [t]
  );

  const settingCurrency = getGlobalSettingRow(module_settings, 'currency');
  const settingTemperature = getGlobalSettingRow(module_settings, 'temperature');
  const settingDateFormat = getGlobalSettingRow(module_settings, 'date_format');
  const settingTimezone = getGlobalSettingRow(module_settings, 'timezone');
  const settingWeatherLocation = getGlobalSettingRow(module_settings, 'weather_location');
  const settingPageSizePc = getGlobalSettingRow(module_settings, 'page_size_pc');
  const settingPageSizeMobile = getGlobalSettingRow(module_settings, 'page_size_mobile');

  const currency = getGlobalSetting(module_settings, 'currency') ?? '€';
  const temperature = getGlobalSetting(module_settings, 'temperature') ?? '°C';
  const dateFormat = getGlobalSetting(module_settings, 'date_format') ?? 'dd.MM.yyyy';
  const timezone = getGlobalSetting(module_settings, 'timezone') ?? 'Europe/Bratislava';
  const weatherLocation = (getGlobalSetting(module_settings, 'weather_location') ?? '').trim();
  const [weatherLocationInput, setWeatherLocationInput] = useState(weatherLocation);
  const [resolvedCoords, setResolvedCoords] = useState(null);
  const [geoStatus, setGeoStatus] = useState('idle'); // 'idle' | 'loading' | 'ok' | 'error'
  useEffect(() => setWeatherLocationInput(weatherLocation), [weatherLocation]);
  useEffect(() => {
    if (!weatherLocation.trim()) {
      setResolvedCoords(null);
      setGeoStatus('idle');
      return;
    }
    setGeoStatus('loading');
    const q = encodeURIComponent(weatherLocation.trim());
    fetch(`https://api.openweathermap.org/geo/1.0/direct?q=${q}&limit=1&appid=${OPENWEATHER_API_KEY}`)
      .then((r) => r.json())
      .then((arr) => {
        if (Array.isArray(arr) && arr[0]?.lat != null && arr[0]?.lon != null) {
          setResolvedCoords({ lat: arr[0].lat, lon: arr[0].lon });
          setGeoStatus('ok');
        } else {
          setResolvedCoords(null);
          setGeoStatus('error');
        }
      })
      .catch(() => {
        setResolvedCoords(null);
        setGeoStatus('error');
      });
  }, [weatherLocation]);
  const pageSizePc = parseInt(getGlobalSetting(module_settings, 'page_size_pc'), 10) || 50;

  function saveWeatherLocation() {
    const v = (weatherLocationInput ?? '').trim();
    if (!settingWeatherLocation) {
      toast?.current?.show({ severity: 'warn', summary: t('UnitsSettings.Toast.SettingUnavailable'), life: 4000, detail: t('UnitsSettings.Toast.WeatherMigrationDetail') });
      return;
    }
    if (v === weatherLocation) return;
    updateSetting(settingWeatherLocation, v);
  }
  const pageSizeMobile = parseInt(getGlobalSetting(module_settings, 'page_size_mobile'), 10) || 25;

  const currencyOptions = useMemo(() => {
    const fromApi = settingCurrency && parseOptions(settingCurrency.options).length > 0
      ? parseOptions(settingCurrency.options)
      : null;
    return fromApi && fromApi.length > 0 ? fromApi : defaultCurrencyOptions;
  }, [settingCurrency, defaultCurrencyOptions]);

  const timezoneOptions = useMemo(() => {
    const fromApi = settingTimezone && parseOptions(settingTimezone.options).length > 0
      ? parseOptions(settingTimezone.options)
      : null;
    return fromApi && fromApi.length > 0 ? fromApi : defaultTimezoneOptions;
  }, [settingTimezone, defaultTimezoneOptions]);
  const temperatureOptions = settingTemperature ? parseOptions(settingTemperature.options) : defaultTemperatureOptions;
  const dateFormatOptions = useMemo(() => {
    const fromApi = settingDateFormat ? parseOptions(settingDateFormat.options) : [];
    if (fromApi.length === 0) return defaultDateFormatOptions;
    return fromApi.map((opt) => {
      const withExample = defaultDateFormatOptions.find((e) => e.value === opt.value);
      return { value: opt.value, label: withExample ? withExample.label : opt.label };
    });
  }, [defaultDateFormatOptions, settingDateFormat]);

  async function updateSetting(setting, value) {
    if (!setting) return;
    const valueStr = String(value);
    if (setting.value === valueStr) return;
    try {
      await postRequest(`/api/global_settings/update/${setting.id}`, { value: valueStr }, true);
      dispatch({ type: 'update_module_setting', id: setting.id, value: valueStr });
      toast?.current?.show({ severity: 'info', summary: t('UnitsSettings.Toast.SavedSummary'), life: 3000, detail: t('UnitsSettings.Toast.SavedDetail') });
    } catch (e) {
      toast?.current?.show({ severity: 'error', summary: t('UnitsSettings.Toast.ErrorSummary'), life: 3000, detail: e?.message || t('UnitsSettings.Toast.SaveFailedDetail') });
    }
  }

  return (
    <div className="relative flex flex-col h-[calc(100vh-137px)] bg-gray-100 overflow-y-auto overflow-x-hidden">
      <div className="p-4 w-full">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
          <h1 className="text-3xl font-semibold text-left text-zinc-900 m-0">
            {t('UnitsSettings.Title')}
          </h1>
          {user?.permissions?.includes('access settings') && (
            <button
              type="button"
              className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-zinc-800 rounded-md hover:bg-zinc-700"
              onClick={() => openChosenNotificationPreferences?.('UnitsSettings')}
            >
              <span className="pi pi-envelope" aria-hidden />
              {t('ServiceTask.Notifications')}
            </button>
          )}
        </div>
        <div className="p-6 bg-white rounded-md shadow-md w-full max-w-3xl">
          <section className="mb-8">
            <h2 className="text-lg font-medium text-zinc-800 mb-3 pb-2 border-b border-gray-200">
              {t('UnitsSettings.SectionDisplay')}
            </h2>
            <p className="text-sm text-gray-500 mb-5">
              {t('UnitsSettings.SectionDisplayHelp')}
            </p>
            <div className="pl-4 flex flex-col gap-3 border-l-2 border-gray-200">
              <div className="flex flex-nowrap items-center gap-3">
                <label className="text-sm font-medium text-gray-800 min-w-0 flex-1">{t('UnitsSettings.LabelCurrency')}</label>
                <Dropdown
                  value={currency}
                  options={currencyOptions}
                  optionLabel="label"
                  optionValue="value"
                  onChange={(e) => updateSetting(settingCurrency, e.value)}
                  className="w-32 border border-gray-300 rounded-md"
                  placeholder={t('UnitsSettings.PlaceholderCurrency')}
                />
              </div>
              <div className="flex flex-nowrap items-center gap-3">
                <label className="text-sm font-medium text-gray-800 min-w-0 flex-1">{t('UnitsSettings.LabelTemperature')}</label>
                <Dropdown
                  value={temperature}
                  options={temperatureOptions}
                  optionLabel="label"
                  optionValue="value"
                  onChange={(e) => updateSetting(settingTemperature, e.value)}
                  className="w-48 border border-gray-300 rounded-md"
                  placeholder={t('UnitsSettings.PlaceholderTemperature')}
                />
              </div>
              <div className="flex flex-nowrap items-center gap-3">
                <label className="text-sm font-medium text-gray-800 min-w-0 flex-1">{t('UnitsSettings.LabelDateFormat')}</label>
                <Dropdown
                  value={dateFormat}
                  options={dateFormatOptions}
                  optionLabel="label"
                  optionValue="value"
                  onChange={(e) => updateSetting(settingDateFormat, e.value)}
                  className="flex-1 max-w-md border border-gray-300 rounded-md"
                  placeholder={t('UnitsSettings.PlaceholderDateFormat')}
                />
              </div>
              <div className="flex flex-nowrap items-center gap-3">
                <label className="text-sm font-medium text-gray-800 min-w-0 flex-1">{t('UnitsSettings.LabelTimezone')}</label>
                <Dropdown
                  value={timezone}
                  options={timezoneOptions}
                  optionLabel="label"
                  optionValue="value"
                  onChange={(e) => updateSetting(settingTimezone, e.value)}
                  className="flex-1 max-w-md border border-gray-300 rounded-md"
                  placeholder={t('UnitsSettings.PlaceholderTimezone')}
                />
              </div>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-lg font-medium text-zinc-800 mb-3 pb-2 border-b border-gray-200">
              {t('UnitsSettings.SectionWeather')}
            </h2>
            <p className="text-sm text-gray-500 mb-5">
              {t('UnitsSettings.SectionWeatherHelp')}
            </p>
            <div className="pl-4 flex flex-col gap-3 border-l-2 border-gray-200">
              <div className="flex flex-nowrap items-center gap-3">
                <label className="text-sm font-medium text-gray-800 min-w-0 flex-1" htmlFor="weather_location">{t('UnitsSettings.LabelLocation')}</label>
                <InputText
                  id="weather_location"
                  value={weatherLocationInput}
                  onChange={(e) => setWeatherLocationInput(e.target.value ?? '')}
                  onBlur={saveWeatherLocation}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      saveWeatherLocation();
                    }
                  }}
                  placeholder={t('UnitsSettings.LocationPlaceholder')}
                  className="flex-1 max-w-md border border-gray-300 rounded-md p-2 text-sm"
                />
              </div>
              {!settingWeatherLocation && (
                <p className="text-sm text-amber-700">
                  {t('UnitsSettings.WeatherMigrationHint')}: <code className="bg-gray-100 px-1 rounded">php artisan migrate</code>
                </p>
              )}
              {settingWeatherLocation && weatherLocation && (
                <p className="text-sm text-gray-600">
                  {t('UnitsSettings.SavedLocation')} <strong>{weatherLocation}</strong>
                </p>
              )}
              {settingWeatherLocation && weatherLocation && geoStatus === 'loading' && (
                <p className="text-sm text-gray-500">{t('UnitsSettings.GeoLoading')}</p>
              )}
              {settingWeatherLocation && weatherLocation && geoStatus === 'ok' && resolvedCoords && (
                <p className="text-sm text-gray-700">
                  {t('UnitsSettings.GeoCoordsLabel')} <span className="font-mono bg-gray-100 px-1.5 py-0.5 rounded">{resolvedCoords.lat.toFixed(5)}, {resolvedCoords.lon.toFixed(5)}</span>
                </p>
              )}
              {settingWeatherLocation && weatherLocation && geoStatus === 'error' && (
                <p className="text-sm text-amber-700">{t('UnitsSettings.GeoError')}</p>
              )}
            </div>
          </section>

          <section>
            <h2 className="text-lg font-medium text-zinc-800 mb-3 pb-2 border-b border-gray-200">
              {t('UnitsSettings.SectionPageSize')}
            </h2>
            <p className="text-sm text-gray-500 mb-5">
              {t('UnitsSettings.SectionPageSizeHelp')}
            </p>
            <div className="pl-4 flex flex-col gap-3 border-l-2 border-gray-200">
              <div className="flex flex-nowrap items-center gap-3">
                <label className="text-sm font-medium text-gray-800 min-w-0 flex-1">{t('UnitsSettings.LabelPc')}</label>
                <Dropdown
                  value={pageSizePc}
                  options={PAGE_SIZE_OPTIONS}
                  optionLabel="label"
                  optionValue="value"
                  onChange={(e) => updateSetting(settingPageSizePc, e.value)}
                  className="w-32 border border-gray-300 rounded-md"
                  placeholder={t('UnitsSettings.PlaceholderCount')}
                />
              </div>
              <div className="flex flex-nowrap items-center gap-3">
                <label className="text-sm font-medium text-gray-800 min-w-0 flex-1">{t('UnitsSettings.LabelMobile')}</label>
                <Dropdown
                  value={pageSizeMobile}
                  options={PAGE_SIZE_OPTIONS}
                  optionLabel="label"
                  optionValue="value"
                  onChange={(e) => updateSetting(settingPageSizeMobile, e.value)}
                  className="w-32 border border-gray-300 rounded-md"
                  placeholder={t('UnitsSettings.PlaceholderCount')}
                />
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
